import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { auth, type User, ApiError } from './api';

type AuthState = {
  user: User | null;
  loading: boolean;
  login: (login: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Probe /api/auth/me on mount. 401 is a normal "not logged in" outcome,
  // not an error to surface to the user.
  useEffect(() => {
    auth.me()
      .then(({ user }) => setUser(user))
      .catch((e) => {
        if (!(e instanceof ApiError) || e.status !== 401) {
          console.error('[auth] me() failed:', e);
        }
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const value: AuthState = {
    user,
    loading,
    login: async (login, password) => {
      const { user } = await auth.login(login, password);
      setUser(user);
    },
    logout: async () => {
      await auth.logout();
      setUser(null);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
